import {
  defineStore,
  ref,
  storeFactory,
  useCategories,
  useCreators,
  useSupabase,
  useUser,
  type GetterType,
} from "#imports";

import type { QueryData } from "@supabase/supabase-js";
import { uniq } from "lodash-es";
import { validate as validateUUID } from "uuid";

export type Map = GetterType<ReturnType<typeof useMaps>["getById"]>;

export const useMaps = defineStore("maps", () => {
  const supabase = useSupabase();
  const categoryStore = useCategories();
  const creatorStore = useCreators();
  const userStore = useUser();

  const selectMaps = (withCount: boolean = false) =>
    supabase
      .from("maps")
      .select(
        `*, spots(id, category_id, place_id)`,
        withCount ? { count: "exact" } : undefined,
      )
      .order("created_at", { ascending: false })
      .throwOnError();

  const selectMapsWithSpots = (withCount: boolean = false) =>
    supabase
      .from("maps")
      .select(
        `*, spots!inner(id, category_id, place_id)`,
        withCount ? { count: "exact" } : undefined,
      )
      .order("created_at", { ascending: false })
      .throwOnError();

  type RawMap = QueryData<ReturnType<typeof selectMaps>>[number];
  const mapMap = (map: RawMap) => ({
    ...map,
    spots: undefined,
    spotIds: map.spots.map((spot) => spot.id),
    categories: categoryStore.getByIds(
      uniq(
        map.spots
          .map((spot) => spot.category_id)
          .filter((id) => typeof id === "string"),
      ),
    ).value,
    placeIds: uniq(
      map.spots
        .map((spot) => spot.place_id)
        .filter((id) => typeof id === "string"),
    ),
  });

  const {
    dataById,
    fetchFactory,
    fetchOneByKeyFactory,
    fetchById,
    fetchByIds,
    getById,
    getByIds,
  } = storeFactory<RawMap, ReturnType<typeof mapMap>>(
    "maps",
    async (payload) => await selectMaps().in("id", payload),
    mapMap,
  );

  /** Fetch and cache recent featured maps */
  const {
    metadata: metadataRecentMaps,
    fetch: fetchRecentMaps,
    result: recentMaps,
  } = fetchFactory<{ limit: number }>(
    async (payload) =>
      await selectMapsWithSpots()
        .order("created_at", { ascending: false })
        .limit(payload.limit),
  );

  const {
    metadata: metadataFeaturedMaps,
    fetch: fetchFeaturedMaps,
    result: featuredMaps,
  } = fetchFactory<{ limit: number }>(
    async (payload) => await selectMapsWithSpots().limit(payload.limit),
  );

  const {
    metadata: metadataBySlug,
    fetch: fetchBySlug,
    result: mapBySlug,
  } = fetchOneByKeyFactory(
    "current_slug",
    async (current_slug) =>
      await selectMaps().eq("current_slug", current_slug).single(),
  );

  const mapBySlugOrId = ref<GetterType<typeof getById>>();
  const fetchBySlugOrId = async (
    slug_or_id: string,
    forceRefetch: boolean = false,
  ) => {
    if (validateUUID(slug_or_id)) {
      await fetchById(slug_or_id, forceRefetch);
      mapBySlugOrId.value = getById(slug_or_id).value;
    } else {
      await fetchBySlug(slug_or_id, forceRefetch);
      mapBySlugOrId.value = mapBySlug.value;
    }
    return mapBySlugOrId;
  };

  const {
    metadata: metadataForCreator,
    fetch: fetchForCreator,
    result: forCreator,
    totalRows: forCreatorTotalRows,
  } = fetchFactory<{
    sortBy?: string;
    sortDirection?: "asc" | "desc" | null;
    limit?: number;
    offset?: number;
    withTotal?: boolean;
  }>(async (payload) => {
    return await selectMaps(payload.withTotal)
      .eq("user_id", userStore.user.id!)
      .order(payload.sortBy ?? "created_at", {
        ascending: payload.sortDirection !== "desc",
      })
      .range(
        payload.offset ?? 0,
        (payload.offset ?? 0) + (payload.limit ?? 20) - 1,
      );
  });

  type MapFilterParams = {
    categories?: string[];
    creators?: string[];
    placeIds?: string[];
    limit?: number;
    offset?: number;
  };

  const {
    metadata: metadataFilteredMaps,
    fetch: fetchFilteredMaps,
    result: filteredMaps,
  } = fetchFactory<MapFilterParams>(async (payload) => {
    let query = selectMapsWithSpots().range(
      payload.offset ?? 0,
      (payload.offset ?? 0) + (payload.limit ?? 20) - 1,
    );

    if (payload.categories && payload.categories.length > 0) {
      const catIds = categoryStore
        .findCategoriesFromSlugs(payload.categories)
        .value.map((cat) => cat.id);
      query = query.in("spots.category_id", catIds);
    }

    if (payload.placeIds && payload.placeIds.length > 0) {
      query = query.in("spots.place_id", payload.placeIds);
    }

    if (payload.creators && payload.creators.length > 0) {
      const creatorIds = await creatorStore.fetchCreatorByHandles({
        handles: payload.creators,
      });
      query = query.in(
        "user_id",
        creatorIds.value.map((creator) => creator.id),
      );
    }

    return await query;
  });

  const {
    metadata: metadataSearch,
    fetch: search,
    result: searchResults,
    totalRows: searchTotalRows,
  } = fetchFactory<{
    search: string;
    sortBy?: string;
    sortDirection?: "asc" | "desc" | null;
    limit?: number;
    offset?: number;
    withTotal?: boolean;
  }>(async (payload) => {
    const search = payload.search.toLowerCase();
    if (search.length === 0) return { data: [], count: 0 };
    return await selectMaps(payload.withTotal)
      .textSearch("fts", search, { type: "websearch" })
      .order(payload.sortBy ?? "created_at", {
        ascending: payload.sortDirection !== "desc",
      })
      .range(
        payload.offset ?? 0,
        (payload.offset ?? 0) + (payload.limit ?? 20) - 1,
      );
  });

  // we must return all refs!
  // we won't use them in our code, but nuxt requires it for SSR
  return {
    dataById, // do not use, only for nuxt SSR cache transfer

    fetchById,
    getById,

    fetchByIds,
    getByIds,

    metadataRecentMaps,
    fetchRecentMaps,
    recentMaps,

    metadataFeaturedMaps,
    fetchFeaturedMaps,
    featuredMaps,

    metadataBySlug,
    fetchBySlug,
    mapBySlug,

    filteredMaps,
    fetchFilteredMaps,
    metadataFilteredMaps,

    mapBySlugOrId,
    fetchBySlugOrId,

    metadataForCreator,
    fetchForCreator,
    forCreator,
    forCreatorTotalRows,

    metadataSearch,
    search,
    searchResults,
    searchTotalRows,
  };
});
